import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Row, Col, ToggleButtonGroup, ToggleButton } from "react-bootstrap";
import PricingCard from "./PricingCard";
import "./TariffModal.css";
import FeaturesComparison from "./FeaturesComparison";
import { GET, POST, PUT, FILE } from 'helpers/api';

const TariffModal = ({ item, onClose, onSave, onPaymentRequired }) => {
  const { t } = useTranslation();
  const currentTariff = Number(item?.tariffId);
  const posId = Number(item?.id);
  const [billingType, setBillingType] = useState("monthly");
  const [tariffChecks, setTariffChecks] = useState({});

  useEffect(() => {
    const upgradeIds = [1, 2, 3].filter(id => id > currentTariff);
    if (!posId || upgradeIds.length === 0) return;

    upgradeIds.forEach(async (tariffId) => {
      try {
        const result = await GET(`/services/web/api/pos/${posId}/tariff-check?newTariffId=${tariffId}`);
        if (result) {
          setTariffChecks(prev => ({ ...prev, [tariffId]: result }));
        }
      } catch (e) {
        // silently ignore — card stays enabled, backend will validate on submit
      }
    });
  }, [posId, currentTariff]);

  const handleBillingToggle = (val) => setBillingType(val);

  const handleChoose = async ({ tariffId, billingType }) => {
    try {
      const response = await POST("/services/web/api/pos/change-tariff", { tariffId, billingType, posId }, { loader: true });
      const data = response?.data || response;
      if (data?.status === "PAYMENT_REQUIRED") {
        onClose?.();
        onPaymentRequired?.({ posId, amountNeeded: data.amountNeeded });
      } else {
        onSave?.();
      }
    } catch (err) {
      const data = err?.response?.data;
      if (data?.status === "PAYMENT_REQUIRED") {
        onClose?.();
        onPaymentRequired?.({ posId, amountNeeded: data.amountNeeded });
      } else {
        console.error("Error updating tariff:", err);
      }
    }
  };

  const tariffs = [
    {
      id: 1,
      bgColor: '#D9D9D950',
      title: t("TARIFF_START") + " 🌱",
      monthly: {
        price: "100.000 so'm",
        period: t("PERIOD_MONTHLY"),
      },
      yearly: {
        price: "1.200.000 so'm",
        period: t("PERIOD_YEARLY"),
      },
      features: [
        { text: t("FEATURE_ADD_PRODUCTS"), type: "♾️" },
        { text: t("FEATURE_TAX_SYSTEM_BASE"), type: "📦" },
        { text: t("FEATURE_EXCEL_UPLOAD"), type: "📥" },
        { text: t("FEATURE_AUTO_INVOICE"), type: "🧾" },
        { text: t("FEATURE_INVENTORY_CONTROL"), type: "🏷️" },
        { text: t("FEATURE_SUPPLIER_ACCOUNTING"), type: "🤝" },
        { text: t("FEATURE_BARCODE_PRINT"), type: "🧾" },
        { text: t("FEATURE_TELEGRAM_CONTROL"), type: "🤖" },
        { text: t("FEATURE_REPORTS"), type: "📊" },
        { text: t("FEATURE_MOBILE_SALES"), type: "📱" },
        { text: t("FEATURE_TAX_INTEGRATION"), type: "🧩" },
        { text: t("FEATURE_PAYMENT_SYSTEMS"), type: "💳" },
      ],
      buttonText: t("BTN_START"),
      buttonColor: "btn-secondary",
    },
    {
      id: 2,
      bgColor: '#FFF3CD ',

      title: t("TARIFF_STANDARD") + " 🪴",
      monthly: {
        price: "150.000 so'm",
        period: t("PERIOD_MONTHLY"),
      },
      yearly: {
        price: "1.800.000 so'm",
        period: t("PERIOD_YEARLY"),
      },
      features: [
        { text: t("FEATURE_ADD_PRODUCTS"), type: "♾️" },
        { text: t("FEATURE_TAX_SYSTEM_BASE"), type: "📦" },
        { text: t("FEATURE_EXCEL_UPLOAD"), type: "📥" },
        { text: t("FEATURE_AUTO_INVOICE"), type: "🧾" },
        { text: t("FEATURE_INVENTORY_CONTROL"), type: "🏷️" },
        { text: t("FEATURE_SUPPLIER_ACCOUNTING"), type: "🤝" },
        { text: t("FEATURE_BARCODE_PRINT"), type: "🧾" },
        { text: t("FEATURE_TELEGRAM_CONTROL"), type: "🤖" },
        { text: t("FEATURE_REPORTS"), type: "📊" },
        { text: t("FEATURE_MOBILE_SALES"), type: "📱" },
        { text: t("FEATURE_TAX_INTEGRATION"), type: "🧩" },
        { text: t("FEATURE_PAYMENT_SYSTEMS"), type: "💳" },
        { text: t("FEATURE_RECOMMENDED_PRODUCTS"), type: "🛍️" },
        { text: t("FEATURE_FROZEN_GOODS"), type: "❄️" },
        { text: t("FEATURE_PROMOS_BUNDLES"), type: "🎁" },
        { text: t("FEATURE_CURRENCY_SUPPORT"), type: "💱" },
        { text: t("FEATURE_EXPENSES_TRACKING"), type: "💸" },
        { text: t("FEATURE_MARKING_SUPPORT"), type: "🏷️" },
      ],
      buttonText: t("BTN_STANDARD"),
      buttonColor: "btn-primary",
    },
    {
      id: 3,
      bgColor: '#D1E7DD',

      title: t("TARIFF_PREMIUM") + " 🌳",
      monthly: {
        price: "250.000 so'm",
        period: t("PERIOD_MONTHLY"),
      },
      yearly: {
        price: "3.000.000 so'm",
        period: t("PERIOD_YEARLY"),
      },
      features: [
        { text: t("FEATURE_ADD_PRODUCTS"), type: "♾️" },
        { text: t("FEATURE_TAX_SYSTEM_BASE"), type: "📦" },
        { text: t("FEATURE_EXCEL_UPLOAD"), type: "📥" },
        { text: t("FEATURE_AUTO_INVOICE"), type: "🧾" },
        { text: t("FEATURE_INVENTORY_CONTROL"), type: "🏷️" },
        { text: t("FEATURE_SUPPLIER_ACCOUNTING"), type: "🤝" },
        { text: t("FEATURE_BARCODE_PRINT"), type: "🧾" },
        { text: t("FEATURE_TELEGRAM_CONTROL"), type: "🤖" },
        { text: t("FEATURE_REPORTS"), type: "📊" },
        { text: t("FEATURE_MOBILE_SALES"), type: "📱" },
        { text: t("FEATURE_TAX_INTEGRATION"), type: "🧩" },
        { text: t("FEATURE_PAYMENT_SYSTEMS"), type: "💳" },
        { text: t("FEATURE_RECOMMENDED_PRODUCTS"), type: "🛍️" },
        { text: t("FEATURE_FROZEN_GOODS"), type: "❄️" },
        { text: t("FEATURE_PROMOS_BUNDLES"), type: "🎁" },
        { text: t("FEATURE_CURRENCY_SUPPORT"), type: "💱" },
        { text: t("FEATURE_EXPENSES_TRACKING"), type: "💸" },
        { text: t("FEATURE_MARKING_SUPPORT"), type: "🏷️" },
        { text: t("FEATURE_LOYALTY"), type: "🎉" },
        { text: t("FEATURE_ANALYTICS"), type: "📊" },
        { text: t("FEATURE_PRODUCT_MOVEMENT"), type: "🔄" },
      ],
      buttonText: t("BTN_PREMIUM"),
      buttonColor: "btn-danger",
    },
  ];

  return (
    <Modal show onHide={onClose} size="xl"
      style={{ zIndex: 1050 }}

    >
      <Modal.Header closeButton>
        <Modal.Title>{t("TARIFF_PLANS")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex justify-content-center">
          <ToggleButtonGroup
            type="radio"
            name="billing"
            value={billingType}
            onChange={handleBillingToggle}
          >
            <ToggleButton id="tbg-monthly" value="monthly" variant="outline-primary">
              {t("MONTHLY")}
            </ToggleButton>
            <ToggleButton id="tbg-yearly" value="yearly" variant="outline-success">
              {t("YEARLY")}
            </ToggleButton>
          </ToggleButtonGroup>
        </div>

        <Row className="py-4 g-4">
          {tariffs.map((tariff) => {
            const priceInfo = billingType === "monthly" ? tariff.monthly : tariff.yearly;
            const isUpgrade = tariff.id > currentTariff;
            const check = isUpgrade ? tariffChecks[tariff.id] : null;

            return (
              <Col md={4} key={tariff.id}>
                <PricingCard
                  id={tariff.id}
                  title={tariff.title}
                  price={priceInfo.price}
                  oldPrice={priceInfo.oldPrice}
                  period={priceInfo.period}
                  isPopular={currentTariff === tariff.id}
                  features={tariff.features}
                  buttonText={currentTariff !== tariff.id ? tariff.buttonText : null}
                  buttonColor={tariff.buttonColor}
                  billingType={billingType}
                  onChoose={handleChoose}
                  bgColor={tariff.bgColor}
                  tariffCheck={check}
                />
              </Col>
            );
          })}
        </Row>

        <h3>{t("FEATURES_HEADING")}</h3>
        <FeaturesComparison />
      </Modal.Body>
    </Modal>
  );
};

export default TariffModal;
